// Client layer (TS-2.5.03). ONE roadmap stat card's own interactivity (decisions.md D-284, owner
// requirement: "showing the number on top may keep people guessing" - the driving case named was
// "At risk / blocked / on hold"). Owns its own open/closed state, the same
// button-plus-its-own-Modal split ProjectDetailCard/ProjectDetailModal already use for the same
// reason - each card decides for itself whether it has anything to show.
//
// A card whose server-reported membership is empty (both `memberProjectKeys` and
// `memberNoteKeys` empty) renders as plain, non-interactive text rather than a button that opens
// an empty dialog: a click that reveals "nothing" is a worse answer than the number the card
// already shows, and it saves every reader from a wasted click on a card that, by construction
// (D-283), can never gain a member later without the number changing too - a stale disabled
// affordance never lingers.
//
// D-285/D-286: a card is now one of TWO kinds, told apart by which list it populated - never by
// its label or colour token, which are presentation the server is free to change independently.
// `memberNoteKeys.length > 0` means the card counted NOTES (the sixth "Open risks & blockers"
// card, the only kind that can ever populate this list per D-285's per-kind invariant), so it
// opens `StatCardNotesModal` instead of D-284's project list; every other card still opens
// `StatCardMembersModal` exactly as before.
import { useMemo, useState } from "react";
import { resolveColor } from "./lib/colorTokens";
import { buildNotesByKey } from "./lib/riskNotes";
import { StatCardMembersModal } from "./StatCardMembersModal";
import { StatCardNotesModal } from "./StatCardNotesModal";
import type { ProjectView, StatCardValue } from "../../api/generated/contracts";
import { strings } from "../../strings";

const S = strings.roadmap.statCardModal;
const NOTES_S = strings.roadmap.notesCardModal;

export interface StatCardProps {
  readonly card: StatCardValue;
  readonly projectsByKey: ReadonlyMap<string, ProjectView>;
}

export function StatCard({ card, projectsByKey }: StatCardProps) {
  const [open, setOpen] = useState(false);
  const isNotesCard = card.memberNoteKeys.length > 0;
  const hasMembers = isNotesCard || card.memberProjectKeys.length > 0;

  // Built lazily (only when this card is a notes card) from the same project data the page
  // already holds for the board - no second fetch, no re-derivation (D-284/D-285's shared rule).
  const notesByKey = useMemo(() => (isNotesCard ? buildNotesByKey(projectsByKey) : null), [isNotesCard, projectsByKey]);

  const value = (
    <span className="eg-stat-card__value" style={{ color: resolveColor(card.colorToken) }}>
      {card.value}
    </span>
  );
  const label = <span className="eg-stat-card__label">{card.label}</span>;

  if (!hasMembers) {
    return (
      <>
        {value}
        {label}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="eg-stat-card__button"
        onClick={() => setOpen(true)}
        aria-label={isNotesCard ? NOTES_S.openAriaLabel(card.value) : S.openAriaLabel(card.value, card.label)}
      >
        {value}
        {label}
      </button>
      {isNotesCard && notesByKey ? (
        <StatCardNotesModal open={open} card={card} notesByKey={notesByKey} onClose={() => setOpen(false)} />
      ) : (
        <StatCardMembersModal open={open} card={card} projectsByKey={projectsByKey} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
